import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { excludeNullAndUndefined, inGroupsOf } from 'utils/array';
import { promiseAllSync } from 'utils/promise';
import { queryClient } from 'utils/query';
import { sentryCaptureException } from 'utils/sentry';

export type BaseUseCollectionOptions = {
    /**
     * The name of the query.  If it changes, the query will reinitialize.
     */
    queryName: string;

    /**
     * Load batch size (defaults to `10`);
     */
    batchSize?: number;

    /**
     * How to handle errors encountered while requesting individual items:
     * - `'none'` (default) — error is thrown (breaks the query)
     * - `'omit'` — failed items are silently excluded from results
     * - `'report'` — failed items are excluded from results but reported to Sentry
     */
    errorHandling?: 'none' | 'omit' | 'report';
};

export type UseCollectionOptions<TItem, TId, TTransform = TItem[]> = Readonly<
    BaseUseCollectionOptions & {
        /**
         * List of ids to load.
         */
        itemIds: TId[];
        /**
         * Callback used to load an item.
         */
        itemQuery: (itemId: TId) => Promise<TItem>;
        /**
         * Can be used to transform a loaded collection after all items have been
         * loaded (example: sort results).
         */
        postTransform?: (results: TItem[]) => TTransform;
    }
>;

export const defaultBatchSize = 10;

export function useQueryCollection<TItem, TId, TTransform = TItem[]>({
    queryName,
    itemIds,
    itemQuery,
    postTransform,
    batchSize = defaultBatchSize,
    errorHandling = 'none',
}: UseCollectionOptions<TItem, TId, TTransform>) {
    const idsKey = useMemo(() => [...itemIds].sort().join(','), [itemIds]);

    const loadItemDefault = async (id: TId) => {
        const cacheKey = [queryName, id];
        const cachedItem = queryClient.getQueryData<TItem>(cacheKey);
        if (cachedItem !== undefined) {
            return cachedItem;
        }
        const item = await itemQuery(id);
        queryClient.setQueryData(cacheKey, item);
        return item;
    };
    const loadItemSwallowError = (id: TId) => loadItemDefault(id).catch(() => undefined);
    const loadItemReportError = (id: TId) =>
        loadItemDefault(id).catch((error) => {
            sentryCaptureException({
                error,
                tags: { queryName },
                context: { query: { itemId: String(id) } },
            });
            return undefined;
        });

    const errorHandlers = {
        none: loadItemDefault,
        omit: loadItemSwallowError,
        report: loadItemReportError,
    };
    const loadItem = errorHandlers[errorHandling];

    const { refetch: queryRefetch, ...query } = useQuery({
        queryKey: [queryName, 'collection', idsKey],

        queryFn: async () => {
            const queryGroups = inGroupsOf(itemIds, batchSize).map((idGroup) => () =>
                Promise.all(idGroup.map(loadItem))
            );

            const resultGroups = await promiseAllSync(queryGroups);

            const flattenedResults = resultGroups.flatMap((results) =>
                excludeNullAndUndefined(results)
            );

            return postTransform ? postTransform(flattenedResults) : flattenedResults;
        },

        placeholderData: keepPreviousData,
    });

    const refetch = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: [queryName],
        });
        return queryRefetch;
    }, [queryName, queryRefetch]);

    useEffect(
        () => () => {
            queryClient.invalidateQueries({
                queryKey: [queryName],
            });
        },
        [queryName]
    );

    return { ...query, refetch };
}
